import { useState, useEffect, useRef } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { githubLight, githubDark } from '@uiw/codemirror-theme-github';
import { useDiagram } from '../../contexts/DiagramContext';
import { useSettings } from '../../contexts/SettingsContext';
import diagramApi from '../../services/diagramApi';
import { 
  FiGrid, 
  FiBarChart2, 
  FiGitBranch, 
  FiUsers, 
  FiCalendar, 
  FiLayers, 
  FiActivity,
  FiBox,
  FiCpu,
  FiDatabase,
  FiPieChart,
  FiMap,
  FiTarget,
  FiTrello,
  FiShare2,
  FiServer,
  FiPackage,
  FiFileText,
  FiShuffle,
  FiTrendingUp,
  FiCheckSquare,
  FiMaximize,
  FiMessageSquare,
  FiSend,
  FiX,
  FiChevronDown,
  FiChevronUp,
  FiUser
} from 'react-icons/fi';
import './DiagramEditor.css';
import { EXAMPLE_TEMPLATES, TEMPLATE_TITLES } from '../../utils/templates';

// Function to convert icon string to React component
const getIconComponent = (iconName) => {
  const iconMap = {
    FiBarChart2: <FiBarChart2 />,
    FiActivity: <FiActivity />,
    FiShuffle: <FiShuffle />,
    FiServer: <FiServer />,
    FiBox: <FiBox />,
    FiCpu: <FiCpu />,
    FiDatabase: <FiDatabase />,
    FiCalendar: <FiCalendar />,
    FiLayers: <FiLayers />,
    FiUsers: <FiUsers />,
    FiTrello: <FiTrello />,
    FiTarget: <FiTarget />,
    FiMap: <FiMap />,
    FiCheckSquare: <FiCheckSquare />,
    FiTrendingUp: <FiTrendingUp />,
    FiShare2: <FiShare2 />,
    FiGitBranch: <FiGitBranch />,
    FiPackage: <FiPackage />,
    FiFileText: <FiFileText />,
    FiPieChart: <FiPieChart />
  };
  
  return iconMap[iconName] || <FiGrid />;
};

// Templates modal component
const TemplatesModal = ({ onSelectTemplate, isOpen, onClose }) => {
  const [activeCategory, setActiveCategory] = useState("Sales Process");
  
  if (!isOpen) return null;

  const handleTemplateSelect = (template) => {
    console.log('Template selected:', template.name);
    onSelectTemplate(template);
    onClose();
  };
  
  const categories = EXAMPLE_TEMPLATES.map(cat => cat.category);
  const currentTemplates = EXAMPLE_TEMPLATES.find(cat => cat.category === activeCategory)?.templates || [];

  return (
    <div className="templates-modal-overlay" onClick={onClose}>
      <div className="templates-modal" onClick={e => e.stopPropagation()}>
        <div className="templates-modal-header">
          <h3>{TEMPLATE_TITLES.preSalesDiagrams}</h3>
          <button className="templates-modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="templates-categories">
          {categories.map((category, index) => (
            <button 
              key={index}
              className={`template-category ${category === activeCategory ? 'active' : ''}`}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
        
        <div className="templates-modal-content">
          {currentTemplates.map((template, index) => (
            <div 
              key={index} 
              className="template-item"
              onClick={() => handleTemplateSelect(template)}
            >
              <div className="template-icon">{getIconComponent(template.icon)}</div>
              <div className="template-info">
                <div className="template-name">{template.name}</div>
                <div className="template-description">
                  {template.description || `A ${template.name.toLowerCase()} diagram for pre-sales presentations`}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const DiagramEditor = () => {
  const { code, updateCode, error } = useDiagram();
  const { settings } = useSettings();
  const [localCode, setLocalCode] = useState(code);
  const [isTemplatesModalOpen, setIsTemplatesModalOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("Sales Process");
  const [showTemplatesSection, setShowTemplatesSection] = useState(false);
  const [selectedCode, setSelectedCode] = useState('');
  const editorRef = useRef(null);
  const codeMirrorRef = useRef(null);

  // Update local code when the code in context changes
  useEffect(() => {
    setLocalCode(code);
  }, [code]);

  // Handle code changes
  const handleChange = (value) => {
    setLocalCode(value);
    updateCode(value);
  };

  // Handle template selection
  const handleTemplateSelect = (template) => {
    setLocalCode(template.code);
    updateCode(template.code);
    setIsTemplatesModalOpen(false);
  };

  // Update the handleAskAI function to use the global state
  const handleAskAI = async () => {
    // This function is no longer needed as the chat is controlled from App.jsx
    console.log("AI chat button in editor clicked - this should be handled by the header button");
  };

  // Get current templates based on active category
  const categories = EXAMPLE_TEMPLATES.map(cat => cat.category);
  const currentTemplates = EXAMPLE_TEMPLATES.find(cat => cat.category === activeCategory)?.templates || [];

  return (
    <div className="diagram-editor">
      <div className="editor-toolbar">
        <h2>Editor</h2>
        <div className="editor-toolbar-actions">
          <button 
            className={`templates-button ${showTemplatesSection ? 'active' : ''}`}
            onClick={() => setShowTemplatesSection(!showTemplatesSection)}
            title="Toggle Templates Section"
          >
            <FiGrid /> {showTemplatesSection ? 'Hide Templates' : 'Show Templates'}
          </button>
        </div>
      </div>
      
      <div className="editor-content" ref={editorRef}>
        <CodeMirror
          value={localCode}
          height="100%"
          theme={settings.theme === 'dark' ? githubDark : githubLight}
          extensions={[javascript({ jsx: true })]}
          onChange={handleChange}
          basicSetup={{
            lineNumbers: settings.showLineNumbers,
            foldGutter: true,
            highlightActiveLine: true,
            highlightSelectionMatches: true,
            autocompletion: true,
          }}
          ref={codeMirrorRef}
        />
      </div>
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Templates section below the editor */}
      {showTemplatesSection && (
        <div className="templates-section">
          <div className="templates-section-header">
            <div className="templates-section-title">
              <h3>{TEMPLATE_TITLES.preSalesDiagrams}</h3>
              <div>
                <button 
                  className="templates-fullscreen-button"
                  onClick={() => setShowTemplatesSection(false)}
                  title="Close Templates"
                  style={{ marginRight: '8px' }}
                >
                  <FiX size={14} />
                </button>
                <button 
                  className="templates-fullscreen-button"
                  onClick={() => setIsTemplatesModalOpen(true)}
                  title="Open in Full Screen"
                >
                  <FiMaximize size={14} />
                </button>
              </div>
            </div>
            <div className="templates-categories-tabs">
              {categories.map((category, index) => (
                <button 
                  key={index}
                  className={`template-category-tab ${category === activeCategory ? 'active' : ''}`}
                  onClick={() => setActiveCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
          
          <div className="templates-grid">
            {currentTemplates.map((template, index) => (
              <div 
                key={index} 
                className="template-card"
                onClick={() => handleTemplateSelect(template)}
              >
                <div className="template-card-icon">{getIconComponent(template.icon)}</div>
                <div className="template-card-info">
                  <div className="template-card-name">{template.name}</div>
                  <div className="template-card-description">
                    {template.description || `A ${template.name.toLowerCase()} diagram for pre-sales presentations`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Keep the modal for full-screen template browsing */}
      <TemplatesModal 
        isOpen={isTemplatesModalOpen}
        onClose={() => setIsTemplatesModalOpen(false)}
        onSelectTemplate={handleTemplateSelect}
      />
    </div>
  );
};

export default DiagramEditor; 